import { BROADCAST_TOGGLE_VIDEO_ACTIVE } from '../Types';
import { setAttendeeCameraStatus } from '@shoflo/studio-redux/actions';
import * as Events from '@shoflo/studio-redux/constants/SocketEvents';
import {
  startLocalVideoTile,
  startVideoInput,
  stopVideoInput
} from '@studio/chime/audioVideo/audioVideoHelpers';
import { getURLPathParams } from '@studio/logger/utils/getURLPathParams';
import { commonWebAnalytics } from '@studio/metrics/client/web/commonWebAnalytics';

/**
 * Toggle the video active state.
 *
 * @param notifyAttendees Send out a CAMERA_STATUS_CHANGE event
 * @param source Indicates who initiated the toggle ('self' or 'host')
 * @returns {(function(*, *, *): void)|*}
 */
function toggleVideoActive(notifyAttendees = true, source = 'self') {
  return (dispatch, getState, socket) => {
    const {
      attendeeCameraStatus,
      meeting: { meetingSession, attendeeResponse },
      videoInputDevice
    } = getState();
    const pathParams = getURLPathParams();

    const broadcastId = pathParams?.broadcastId;
    const isHostToggle = source === 'host';

    const action = {
      type: BROADCAST_TOGGLE_VIDEO_ACTIVE,
      request: async () => {
        const attendeeId = attendeeResponse.Attendee.AttendeeId;
        const attendeeVideoActive = attendeeCameraStatus[attendeeId];

        // Determine the new state
        const newCameraState = !attendeeVideoActive;

        // Prepare SLI payload based on source
        const sliPayload = isHostToggle
          ? {
              // Schema for host_toggles_speaker_camera_from_source_tile
              toggleState: newCameraState ? 'ON' : 'OFF',
              speakerId: attendeeId,
              speakerTitle:
                attendeeResponse?.Attendee?.ExternalUserId || 'Unknown',
              toggledSpeakerRole: 'speaker'
            }
          : {
              // Schema for camera_toggled
              cameraState: newCameraState ? 'ON' : 'OFF'
            };

        // Start SLI measurement
        if (isHostToggle) {
          commonWebAnalytics.startStudioHostTogglesSpeakerCameraFromSourceTile(
            sliPayload
          );
        } else {
          commonWebAnalytics.startStudioCameraToggled(sliPayload);
        }

        if (attendeeVideoActive) {
          // presenter is trying to stop video
          console.log('Stop video');

          await stopVideoInput(meetingSession);

          // End SLI measurement on success
          if (isHostToggle) {
            commonWebAnalytics.endStudioHostTogglesSpeakerCameraFromSourceTile(
              sliPayload
            );
          } else {
            commonWebAnalytics.endStudioCameraToggled(sliPayload);
          }
        } else {
          // presenter is trying to start video
          try {
            await startVideoInput(meetingSession, videoInputDevice);
            // Use imported helper to start local video tile and obtain tileId
            const tileId = startLocalVideoTile(meetingSession);
            if (!tileId || tileId === -1) {
              throw new Error(`Invalid tileId ${tileId} returned`);
            }

            // End SLI measurement on success
            if (isHostToggle) {
              commonWebAnalytics.endStudioHostTogglesSpeakerCameraFromSourceTile(
                sliPayload
              );
            } else {
              commonWebAnalytics.endStudioCameraToggled(sliPayload);
            }
          } catch (err) {
            console.error('Failed to start local video tile', {
              error: err?.message || String(err),
              attendeeId,
              videoInputDevice
            });

            // End SLI measurement on error
            if (isHostToggle) {
              commonWebAnalytics.endStudioHostTogglesSpeakerCameraFromSourceTile(
                { ...sliPayload, error: err?.message }
              );
            } else {
              commonWebAnalytics.endStudioCameraToggled({
                ...sliPayload,
                error: err?.message
              });
            }

            // Early return so we don't dispatch ON state if tile failed
            return {
              isVideoActive: attendeeVideoActive
            };
          }
        }
        dispatch(setAttendeeCameraStatus(attendeeId, !attendeeVideoActive));

        if (notifyAttendees) {
          socket.emit(Events.CAMERA_STATUS_CHANGE, {
            attendeeId,
            broadcastId,
            isCameraOn: !attendeeVideoActive
          });
        }
        return {
          isVideoActive: !attendeeVideoActive
        };
      }
    };

    dispatch(action);
  };
}

export default toggleVideoActive;
